import { WatchQueryFetchPolicy } from '@apollo/client'
import { FinancialWorkspaceValueObjectsFinanceTypeEnum } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import {
  InsightTableGroupBy,
  PaginatedTableItem,
} from 'modules/finance/components/FinancialWorkspace/types/table'
import { useCachedData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/CachedDataProvider'
import {
  dataToRow,
  mergeDataAndComparisonData,
} from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/lib/dataToRow'
import {
  Variables,
  getFilteredVariables,
} from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/lib/filteredVariables'
import { FinancialWorkspaceResource } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/types/data'

import { useFinanceSummaries } from './useFinanceSummaries'

interface Props {
  financeType: FinancialWorkspaceValueObjectsFinanceTypeEnum
  resource?: FinancialWorkspaceResource
  childrenType: InsightTableGroupBy
}

interface FetchResourceProps {
  groupBy: InsightTableGroupBy
}

export const useFinanceResource = ({ financeType, resource, childrenType }: Props) => {
  const { data, setData } = useCachedData()
  const { formattedState, hasComparisonDate } = useFinancialWorkspace()

  const { loading, fetchFinanceSummaries, filterVariables } = useFinanceSummaries({
    skip: !resource,
    resource: resource,
    financeTypes: [financeType],
  })

  const {
    loading: comparisonLoading,
    filterVariables: comparisonFilterVariables,
    fetchFinanceSummaries: fetchComparisonFinanceSummaries,
  } = useFinanceSummaries({
    compareTo: true,
    skip: !resource,
    resource: resource,
    financeTypes: [financeType],
  })

  const fetchParams = useUnsafeCallback(
    (
      variables: Variables,
      resourceGroupBy: InsightTableGroupBy
    ): { variables: Variables; fetchPolicy: WatchQueryFetchPolicy } => {
      return {
        variables: {
          ...variables,
          financeTypes: [financeType],
          groupBy: resourceGroupBy,
          ...getFilteredVariables({
            resource,
            formattedState,
            customCategoryIds: filterVariables.customCategoryIds,
            agreementDataTypes: filterVariables.agreementDataTypes || [],
          }),
        },
        fetchPolicy: 'no-cache',
      }
    },
    [filterVariables, financeType, resource, formattedState]
  )

  const fetchResourceChildren = useUnsafeCallback(
    async ({ groupBy: resourceGroupBy }: FetchResourceProps) => {
      const newData = await fetchFinanceSummaries(fetchParams(filterVariables, resourceGroupBy))

      return newData
    },
    [fetchFinanceSummaries, filterVariables, fetchParams]
  )

  const fetchComparisonResourceChildren = useUnsafeCallback(
    async ({ groupBy: resourceGroupBy }: FetchResourceProps) => {
      const newData = await fetchComparisonFinanceSummaries(
        fetchParams(comparisonFilterVariables, resourceGroupBy)
      )

      return newData
    },
    [fetchComparisonFinanceSummaries, comparisonFilterVariables, fetchParams]
  )

  const onLazyChildren = useUnsafeCallback(
    async (
      id: string | number,
      customKey?: string
    ): Promise<{
      loading: boolean
      data: PaginatedTableItem[]
    }> => {
      const key = customKey || `children-${resource?.type}-${id}-${financeType}`

      if (data[key]) {
        return {
          data: data[key]?.data ?? [],
          loading: false,
        }
      }

      const fetch = async () => {
        return fetchResourceChildren({ groupBy: childrenType })
      }

      const fetchComparison = async () => {
        return hasComparisonDate
          ? fetchComparisonResourceChildren({ groupBy: childrenType })
          : undefined
      }

      const newData = await fetch()
      const newComparisonData = await fetchComparison()

      const { data: mergedData, comparisonData: mergedComparisonData } = mergeDataAndComparisonData(
        newData.data?.financialWorkspace.financeFactSummariesConnection.edges.map(
          ({ node }) => node
        ),
        newComparisonData?.data?.financialWorkspace.financeFactSummariesConnection.edges.map(
          ({ node }) => node
        )
      )

      const formattedData =
        mergedData.map((summary, index) => dataToRow(summary, mergedComparisonData[index])) ?? []

      const onRefetch = async () => {
        await fetch()
        await fetchComparison()
      }

      setData({
        ...data,
        [key]: {
          visible: true,
          loading: false,
          refetch: onRefetch,
          data: formattedData,
        },
      })

      return {
        loading: false,
        data: formattedData,
      }
    },
    [
      data,
      setData,
      resource,
      financeType,
      childrenType,
      hasComparisonDate,
      fetchResourceChildren,
      fetchComparisonResourceChildren,
    ]
  )

  return {
    onLazyChildren,
    fetchResourceChildren,
    fetchComparisonResourceChildren,
    fetchResource: fetchFinanceSummaries,
    fetchComparisonResource: fetchComparisonFinanceSummaries,
    loading: loading || (hasComparisonDate && comparisonLoading),
  }
}
